import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { TrendingUp, AlertCircle, Plus, Loader2 } from 'lucide-react'
import { useInventoryItems, useLowStockAlerts } from '@/hooks/inventory'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'

function InventoryAlert() {
  const navigate = useNavigate()
  const { data: items, isLoading, error } = useInventoryItems()
  const { alerts, criticalCount, warningCount } = useLowStockAlerts()

  // Generate alerts from inventory data
  const alertList = useMemo(() => {
    if (!items || !alerts) return []

    return alerts.map((alert) => {
      const item = items.find((i) => i.skuCode === alert.skuCode)
      return {
        ...alert,
        id: item?.id || 0,
        specification: item?.specification || '',
        warehouse: item?.warehouseCode || '南京中心仓',
        unitPrice: 0, // Not in current model
        urgency: alert.level === 'critical' ? 'critical' : alert.level === 'warning' ? 'warning' : 'info',
      }
    })
  }, [items, alerts])

  const urgencyConfig = {
    critical: {
      bg: 'bg-red-50 border-red-100',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      textColor: 'text-red-600',
      btnBg: 'bg-red-500 hover:bg-red-600',
      label: '严重',
    },
    warning: {
      bg: 'bg-orange-50 border-orange-100',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      textColor: 'text-orange-600',
      btnBg: 'bg-orange-500 hover:bg-orange-600',
      label: '警告',
    },
    info: {
      bg: 'bg-amber-50 border-amber-100',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      textColor: 'text-amber-600',
      btnBg: 'bg-amber-500 hover:bg-amber-600',
      label: '提醒',
    },
  }

  const handleBatchRestock = () => {
    navigate('/supply-chain/purchase/create', {
      state: {
        source: 'inventory-alert',
        preselectedItems: alertList.map((alert) => ({
          skuCode: alert.skuCode,
          partName: alert.partName,
          quantity: Math.max(alert.shortage, 1),
        })),
      },
    })
  }

  const handleRestock = (skuCode: string, partName: string) => {
    navigate('/supply-chain/purchase/create', {
      state: {
        source: 'inventory-alert',
        preselectedItems: [
          {
            skuCode,
            partName,
            quantity: Math.max(alertList.find((alert) => alert.skuCode === skuCode)?.shortage ?? 1, 1),
          },
        ],
      },
    })
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载预警数据..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message="加载失败，请重试" />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/supply-chain/inventory/list" className="p-2 hover:bg-gray-100 rounded-lg transition">
              <TrendingUp className="w-5 h-5 text-gray-600 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">库存预警</h1>
              <p className="text-sm text-gray-500 mt-1">低于安全库存的配件列表</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleBatchRestock}
              className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              创建补货申请
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 rounded-2xl p-4 border border-red-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
              <div className="text-xs text-gray-600">严重缺货 (零库存)</div>
            </div>
          </div>
        </div>
        <div className="bg-orange-50 rounded-2xl p-4 border border-orange-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{warningCount}</div>
              <div className="text-xs text-gray-600">库存偏低</div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{alertList.length}</div>
              <div className="text-xs text-gray-600">总预警数</div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">预警配件列表</h2>
            <div className="text-sm text-gray-500">
              共 <span className="font-bold text-gray-900">{alertList.length}</span> 条预警
            </div>
          </div>
        </div>
        {alertList.length === 0 ? (
          <div className="p-5">
            <EmptyData description="暂无库存预警，所有配件库存正常" />
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {alertList.map((alert) => {
              const config = urgencyConfig[alert.urgency as keyof typeof urgencyConfig]
              return (
                <div key={alert.id} className={`flex items-center justify-between p-4 rounded-xl border ${config.bg}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${config.iconBg} rounded-lg flex items-center justify-center`}>
                      <AlertCircle className={`w-6 h-6 ${config.iconColor}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{alert.partName}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${config.iconBg} ${config.textColor}`}
                        >
                          {config.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        <span className="font-mono">{alert.skuCode}</span>
                        {alert.specification && (
                          <>
                            <span className="mx-2">·</span>
                            <span>{alert.specification}</span>
                          </>
                        )}
                        <span className="mx-2">·</span>
                        <span>{alert.warehouse}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className={`text-lg font-bold ${config.textColor}`}>
                        {alert.currentQty}
                      </div>
                      <div className="text-xs text-gray-500">当前</div>
                    </div>
                    <div className="text-gray-300">/</div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-700">{alert.safetyStockQty}</div>
                      <div className="text-xs text-gray-500">安全</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold ${config.textColor}`}>
                        -{alert.shortage}
                      </div>
                      <div className="text-xs text-gray-500">缺口</div>
                    </div>
                    <div className="w-px h-8 bg-gray-200"></div>
                    <div>
                      <button
                        onClick={() => handleRestock(alert.skuCode, alert.partName)}
                        className={`px-4 py-2 ${config.btnBg} text-white text-sm font-medium rounded-lg transition`}
                      >
                        申请补货
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default InventoryAlert
